import React, {useEffect, useRef, useState} from "react";
import {
  ActivationAlgo,
  AutoTradeHelper,
  AutoTradePositionTypeEnum,
  AutoTradeStatusEnum,
  AutoTradeStatusEnumHelper,
  CumulativeChartLines,
  IAutoTrade,
  SignalAlertPerfTypeEnum,
  SignalAlertPerfTypeEnumHelper,
  StockDirectionType
} from "predictagram-lib";
import {
  adminApiServiceCommon,
  adminAutoTradeSetupService,
  adminAutoTradesService
} from "../../../services/AdminApiService";
import {useApiServiceState} from "../../../_hooks/useApiServiceState";
import {Spinner} from "../../common/Spinner";
import {ArrowClockwise} from "react-bootstrap-icons";
import {StockHelper} from "../../../_utils/stock.helper";
import {DatePickerTzEst} from "../../common/DatePickerTz";
import {CumulativeChart} from "../common/CumulativeChart";
import {
  CumulativeChartSignal,
  getAnalysisDefaultLines,
  getChartOptions,
  ICumeDataSearchOptions,
  linesAndAnnotationsOptions
} from "../common/helper";
import {TRADE_CLOSE_COLOR, TRADE_OPEN_COLOR} from "../common/ColoredSignal";
import {IAutoTradeSetupEx} from "../../../interfaces/IAutoTradeSetupEx";
import {SignalAlertPerfScoreFilter} from "../common/filters/SignalAlertPerfScoreFilter";
import {DropDownGeneric} from "../../common/form/DropDownGeneric";
import StatusEnum = ActivationAlgo.StatusEnum;
import {ActivationComboDropDown} from "../common/filters/ActivationComboDropDown";
import {Modal} from "react-bootstrap";
import {colorize, formatUSCurrency} from "./Trades";
import {NewYorkDate} from "../../common/NewYorkDate";
import {ManualAddClose} from "./ManualAddClose";
import {SecurityTypeDropDown} from "../common/filters/SecurityTypeDropDown";

export const AdminTradesDashboard: React.FunctionComponent = () => {
  const getStockDay = (dt:Date)=>StockHelper.findTradingDay(dt, -1, true)
  const refStockDay = useRef(getStockDay(new Date()));
  const refStatus = useRef(AutoTradeStatusEnum.ACTIVE);
  const [stockDay,setStockDay] = useState(getStockDay(new Date()));
  const {items, state, setSupplier, reload} = useApiServiceState(()=>supplier());
  // const [signalActivationCombo, setSignalActivationCombo] = useState<number[]|undefined>(undefined);
  const [activationCombos, setActivationCombos] = useState<any[]>([]);
  const [securityTypeFilter, setSecurityTypeFilter] = useState();
  const [signalAlertPerfFilter, setSignalAlertPerfFilter] = useState<SignalAlertPerfTypeEnum | undefined>(undefined);
  const refAlgo = useRef(signalAlertPerfFilter);
  const refActivationCombo = useRef(undefined);
  const refSecurityType = useRef(undefined);

  const [selectedTrade, setSelectedTrade] = useState<IAutoTrade>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const supplier = async() => {
    const dataRaw = await adminAutoTradesService.getFilteredData({
      startTime: refStockDay.current.startAt(),
      endTime: refStockDay.current.endAt(),
      autoTradeStatusIds: refStatus.current ? [refStatus.current] : undefined,
      signalAlertActivationAlgoTypeIds: refAlgo.current ? [refAlgo.current] : undefined,
      signalAlertActivationComboIds: refActivationCombo.current,
      autoTradeSecurityTypeId: refSecurityType.current,
    });
    const data = dataRaw;
    const setupIds = [...new Set(data.map(t=>t.setupId)).values()];
    const setups = await Promise.all(setupIds.map(id=>adminAutoTradeSetupService.getByIdWithSignalAlert(id)));
    return { trades: data, setups: setups};
  }

  const onClickAnnotation = async(e:{annotation:{text?:string}}, trades:IAutoTrade[]) =>{
    const text = e?.annotation?.text;
    if (!text || !text.startsWith('T')) {
      return;
    }
    const tradeId = +text.replace('T', '');
    if (!tradeId) {
      return;
    }
    const trade = trades?.[tradeId-1];
    if (!trade) {
      return;
    }

    setSelectedTrade(trade);
    setShowModal(true);
  }

  const handleModalClose = ()=>{
    setShowModal(false);
    setSelectedTrade(undefined);
    console.debug('closed modal');
  }

  const renderCharts = (data: { setups: IAutoTradeSetupEx[]; trades: IAutoTrade[] })=>{
    const perSymbol = new Map<number,{
      symbolId: number,
      symbolName: string,
      signals:CumulativeChartSignal[],
      trades:IAutoTrade[]
      lines: Set<CumulativeChartLines>,
    }>();
    for (const [idx,t] of data.trades.entries()) {
      if (!perSymbol.has(t.stockSymbolId)) {
        perSymbol.set(t.stockSymbolId, {
          symbolName: t.stockSymbolName,
          symbolId: t.stockSymbolId,
          signals: [],
          trades: [],
          lines: new Set(),
        });
      }
      const ps = perSymbol.get(t.stockSymbolId);
      if (!ps) {
        continue;
      }
      const isLong = t.positionTypeId===AutoTradePositionTypeEnum.LONG;
      const tradeSetup = data.setups.find(ts=>ts.id===t.setupId);
      if (tradeSetup) {
        getAnalysisDefaultLines(tradeSetup.signalAlert.data).map(l=>ps.lines.add(l));
      }

      ps.trades.push(t);
      const tId = ps.trades.length-1;
      ps.signals.push({
        text: 'T' + (tId+1), // + 'o',
        x: t.openAt,
        y: t.openPrice,
        direction: isLong ?StockDirectionType.UP:StockDirectionType.DOWN,
        color: TRADE_OPEN_COLOR,
      });
      if (t.statusId===AutoTradeStatusEnum.CLOSED) {
        ps.signals.push({
          // text: 'T' + (idx+1) + 'c',
          x: t.closeAt,
          y: t.closePrice,
          direction: isLong ? StockDirectionType.DOWN: StockDirectionType.UP,
          color: TRADE_CLOSE_COLOR,
        });
      }

    }
    return [...perSymbol.values()].map((v,idx)=>{
      return <div key={'chart'+idx} style={{ width: "100%", height: "550px" }}>
        {v.symbolName}
        <CumulativeChart
          cumeDataSearchOptions={{
            startTime: refStockDay.current.openAt(),
            endTime: refStockDay.current.closeAt(),
            stockSymbolId:v.symbolId,
          } as ICumeDataSearchOptions}
          params={{
            // showBaseSignals: [],
            extraSignals: v.signals,
            showLines: [...v.lines.values()],
            showOptions: getChartOptions(linesAndAnnotationsOptions),
          }}
          onClickAnnotation={(e)=>onClickAnnotation(e, v.trades)}
        />

      </div>
    })
  }
  const renderTradeDetails= (trade:IAutoTrade)=>{
    return (<>
      <div className="d-flex flex-column gap-1 mt-3 me-2 align-items-center px-3 text-black text-12" title="Show Order Details">
        <table className="table table-striped table-hover">
          <thead>
          <tr>
            <td>IBKR Open Price</td>
            <td>IBKR Close Price</td>
            <td>IBKR P/L</td>
            <td>Opened On</td>
            <td>Closed On</td>
            <td></td>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td key='ibkrOpenPrice' className="text-end">{formatUSCurrency(trade.ibkr.openPriceAvg)}</td>
            <td key='ibkrClosePrice' className="text-end">{formatUSCurrency(trade.ibkr.closePriceAvg)}</td>
            <td key='ibkrProfiLoss' className="text-end">{colorize(formatUSCurrency(AutoTradeHelper.calcRealizedProfitLossTradeIbkr(trade)))}</td>
            <td key='opened'><NewYorkDate dateInMs={trade.openAt * 1000} /></td>
            <td key='closed'>{trade.closeAt && <NewYorkDate dateInMs={trade.closeAt * 1000} />}</td>
            <td key='ibkr'>
              <ManualAddClose trade={trade}/>
              {/*<div className="d-flex gap-2">*/}
              {/*  {trade.ibkr.openOrders.length > 0 && <button className="btn btn-primary text-13" onClick={() => confirmAdd(trade)}><PlusCircle title="Add Trade" /></button>}*/}
              {/*  {trade.statusId === AutoTradeStatusEnum.ACTIVE &&*/}
              {/*    <button className="btn text-13 bg-red text-white" onClick={() => confirmClose(trade)}><XCircle title="Close Trade" /></button>*/}
              {/*  }*/}
              {/*</div>*/}
            </td>
          </tr>
          </tbody>
        </table>

      </div>
    </>)
  }

  return (
    <>
      <div key={'header'} className="d-flex justify-content-between align-items-end">
        <div className="page-title">Trades Dashboard</div>
      </div>
      <div key={'data'} className="d-flex align-items-center gap-4 justify-content-center">
        <div key={'f-date'}>
          <div key={'date-label'} className="fw-bold">Date</div>
          <DatePickerTzEst
            dateFormat="MM/dd/yyyy"
            onChange={(date: Date) => {
              refStockDay.current = getStockDay(date);
              setStockDay(refStockDay.current);
            }}
            selected={new Date(stockDay.openAt()*1000)}
          />
        </div>
        <div key={'f-2'}>
          <div key={'f-status'}>
            <div key={'status-label'} className="fw-bold">Status:</div>
            <select defaultValue={refStatus.current as any} onChange={(e)=>refStatus.current=(e.target.value ? parseInt(e.target.value):'') as any}>
              <option value=''>All</option>
              {[...AutoTradeStatusEnumHelper.names.entries()].map(s=><option value={s[0]}>{s[1]}</option>)}
            </select>
          </div>
          <SecurityTypeDropDown value={securityTypeFilter} onChange={(v)=>{setSecurityTypeFilter(v as any);refSecurityType.current=v as any}}  label={<div className="fw-bold">Security Type</div>} name='securityTypeId'/>
        </div>
        <div key={'f-algo'}>
          <SignalAlertPerfScoreFilter
            value={signalAlertPerfFilter}
            onChange={(v)=>{setSignalAlertPerfFilter(v as SignalAlertPerfTypeEnum);refAlgo.current=v as any}}/>
        </div>
        {/*<DropDownGeneric style={{width:'200px'}} value={signalActivationCombo} multiple={true}  label={<div className="fw-bold">Act. Combo</div>} name={'combo'} options={activationCombos}*/}
        {/*                 onChange={(v)=>{setSignalActivationCombo(v as any);refActivationCombo.current=v as any}}*/}
        {/*/>*/}
        <ActivationComboDropDown multiple={true} onChange={v=>refActivationCombo.current=v as any}/>
        <div key={'submit'}><button className="btn btn-primary" onClick={() => reload()}><ArrowClockwise /></button></div>
      </div>
      <div key={'space'}>&nbsp;</div>
      {state.isLoading() && <Spinner />}
      {state.isError() && <div key={'err-msg'}>{state.error()?.message}</div>}
      {state.isLoaded() && items &&
        renderCharts(items)
      }

      {selectedTrade &&
          <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
            <Modal.Body className="bg-white">
              <Modal.Header closeButton>
                {/*<Modal.Title>Modal title</Modal.Title>*/}
              </Modal.Header>
              {renderTradeDetails(selectedTrade as IAutoTrade)}
              {/*<div className="d-flex align-items-end"><button type="button" className="btn btn-secondary text-12" onClick={handleModalClose}>Close</button></div>*/}
            </Modal.Body>
          </Modal>
      }
    </>
  );
};


